import i18n from '@dragonfruit/i18n';
import { RENEWABLE } from '@dragonfruit/constants/planTypes';
import {
  formatPriceNumber,
  formatPriceWithCurrency,
} from '@dragonfruit/utils/priceParser';

/**
 * check if the targe payment type exists in all the plan list.
 * this logic is used to decide whether to show price in plan lis at purchase page.
 * @param {object} planData
 * @param {string} targetMethodType
 */

export const checkPaymentTypeInPlanList = (planList, targetMethodType) => {
  return planList.every(({ paymentMethods }) =>
    paymentMethods.some(({ methodType }) => methodType === targetMethodType)
  );
};
export const checkPaymentTypeInCollectionList = (
  collectionList,
  targetMethodType
) => {
  return collectionList.every(({ plan: { paymentMethods } }) =>
    paymentMethods.some(({ methodType }) => methodType === targetMethodType)
  );
};

/**
 * return price unit crowdin string for displaying.
 * current logic:
 * 1. Normal Subscription --> return return the monthly price.
 * 2. TW Annual plan (Contract) --> return return the yearly price.
 * 3. HK Annual plan (Contract) --> return return the monthly price.
 * @param {object} priceInfo
 * @param {string|number} priceInfo.billingCycle
 * @param {string|number} priceInfo.contractPeriod
 * @param {string|number} priceInfo.price
 * @param {string} priceInfo.currency
 * @param {string} priceInfo.displayCurrency
 */

export const getPriceUnitString = ({
  billingCyclePerMonths,
  contractPeriod,
  isContract,
  price,
  currency,
  displayCurrency,
}) => {
  const displayPrice = formatPriceNumber(currency, price);
  const displayPriceWithCurrency = formatPriceWithCurrency(
    currency,
    displayCurrency,
    price
  );

  const monthlyDisplayUnit =
    billingCyclePerMonths > 1
      ? i18n.t('common:common.priceUnit_plural', {
          count: billingCyclePerMonths,
        })
      : i18n.t('common:common.priceUnit');

  if (!isContract) {
    return {
      displayPrice,
      displayCurrency,
      displayPriceWithCurrency,
      displayPriceUnit: monthlyDisplayUnit,
    };
  }

  const billingTimes = contractPeriod / billingCyclePerMonths;
  const totalPrice = billingTimes * price;

  const displayTotalPrice = formatPriceNumber(currency, totalPrice);
  const displayTotalPriceWithCurrency = formatPriceWithCurrency(
    currency,
    displayCurrency,
    totalPrice
  );

  const yearlyDisplayUnit =
    billingTimes > 1
      ? i18n.t('common:common.priceUnit_year_plural', {
          count: billingTimes,
        })
      : i18n.t('common:common.priceUnit_year');

  // TW Annual Plan
  if (contractPeriod === 12 && billingCyclePerMonths === contractPeriod) {
    return {
      displayCurrency,
      displayPrice: displayTotalPrice,
      displayPriceWithCurrency: displayTotalPriceWithCurrency,
      displayPriceUnit: yearlyDisplayUnit,
    };
  }

  return {
    displayPrice,
    displayCurrency,
    displayPriceWithCurrency,
    displayPriceUnit: monthlyDisplayUnit,
  };
};

/**
 * return subscription description string like "N-month plan, billed every N months"
 * @param {number} billingCyclePerMonths
 * @param {number} contractPeriod
 * @param {boolean} isContract
 * @param {string} planType
 */

export const getSubscriptionDescription = ({
  billingCyclePerMonths,
  contractPeriod,
  isContract,
  planType,
}) => {
  // Normal Subscription
  if (!isContract) {
    const isRenewableMonthlyPlan =
      planType === RENEWABLE && billingCyclePerMonths === 1;

    return isRenewableMonthlyPlan
      ? i18n.t('premium:premium.subscriptions.planNameMonthly')
      : i18n.t('premium:premium.subscriptions.planNameMonthlyPlural', {
          count: billingCyclePerMonths,
        });
  }

  // Annually Contract Subscription
  // HK Annual Plan
  if (contractPeriod === 12 && billingCyclePerMonths === 1) {
    return i18n.t('premium:premium.subscriptions.planNameAnnualBilledMonthly');
  }

  // TW Annual Plan
  if (contractPeriod === 12 && billingCyclePerMonths === 12) {
    return i18n.t('premium:premium.subscriptions.planNameAnnual');
  }

  if (contractPeriod === 12 && billingCyclePerMonths > 1) {
    return i18n.t(
      'premium:premium.subscriptions.planNameAnnualBilledMonthlyPlural',
      {
        count: billingCyclePerMonths,
      }
    );
  }

  if (contractPeriod === billingCyclePerMonths) {
    return i18n.t('premium:premium.subscriptions.planNameMonthlyPlural', {
      count: billingCyclePerMonths,
    });
  }

  if (contractPeriod > billingCyclePerMonths && billingCyclePerMonths > 1) {
    return i18n.t(
      'premium:premium.subscriptions.planNameMonthlyBilledMonthlyPlural',
      {
        count: billingCyclePerMonths,
        contractCount: contractPeriod,
      }
    );
  }

  if (contractPeriod > billingCyclePerMonths && billingCyclePerMonths === 1) {
    return i18n.t(
      'premium:premium.subscriptions.planNameMonthlyBilledMonthly',
      {
        count: contractPeriod,
      }
    );
  }
};

/**
 * return price description string like "Pay N/year (M/month)"
 * @param {number} billingCyclePerMonths
 * @param {number} contractPeriod
 * @param {boolean} isContract
 * @param {string} planType
 */

export const getSubscriptionPriceDescription = ({
  billingCyclePerMonths,
  contractPeriod,
  isContract,
  planType,
  price,
  currency,
  displayPriceWithCurrency,
}) => {
  // Normal Subscription
  if (!isContract) {
    const isRenewableMonthlyPlan =
      planType === RENEWABLE && billingCyclePerMonths === 1;

    return isRenewableMonthlyPlan
      ? i18n.t('premium:premium.subscriptions.monthlySingular', {
          price: displayPriceWithCurrency,
        })
      : i18n.t('premium:premium.subscriptions.monthlyPlural', {
          price: displayPriceWithCurrency,
          count: billingCyclePerMonths,
        });
  }

  // Annually Contract Subscription
  const isAnnually = value => value % 12 === 0;
  const averagePrice = price / billingCyclePerMonths;
  const displayAveragePrice = formatPriceNumber(currency, averagePrice);

  // TW Annual Plan
  if (contractPeriod === 12 && billingCyclePerMonths === 12) {
    return i18n.t('premium:premium.subscriptions.annualBilledOnceYear');
  }

  // HK Annual Plan
  if (
    (contractPeriod === 12 && billingCyclePerMonths > 1) ||
    (contractPeriod > billingCyclePerMonths && billingCyclePerMonths === 1)
  ) {
    return i18n.t('premium:premium.subscriptions.annualBilledMonthlySingular', {
      price: displayPriceWithCurrency,
      contractCount: contractPeriod,
    });
  }

  if (
    contractPeriod > billingCyclePerMonths &&
    isAnnually(billingCyclePerMonths) &&
    isAnnually(contractPeriod)
  ) {
    const isOneYear = billingCyclePerMonths === 12;
    return isOneYear
      ? i18n.t('premium:premium.subscriptions.annualBilledYearlySingular', {
          price: displayPriceWithCurrency,
          contractCount: contractPeriod,
          avgPrice: displayAveragePrice,
        })
      : i18n.t('premium:premium.subscriptions.annualBilledYearlyPlural', {
          price: displayPriceWithCurrency,
          contractCount: contractPeriod,
          count: billingCyclePerMonths,
          avgPrice: displayAveragePrice,
        });
  }

  if (contractPeriod === billingCyclePerMonths) {
    return isAnnually(contractPeriod)
      ? i18n.t('premium:premium.subscriptions.annualBilledOnceYears', {
          price: displayPriceWithCurrency,
          count: billingCyclePerMonths,
          avgPrice: displayAveragePrice,
        })
      : i18n.t('premium:premium.subscriptions.annualBilledOnceMonths', {
          price: displayPriceWithCurrency,
          count: billingCyclePerMonths,
          avgPrice: displayAveragePrice,
        });
  }

  if (contractPeriod > billingCyclePerMonths) {
    return i18n.t('premium:premium.subscriptions.annualBilledMonthlyPlural', {
      price: displayPriceWithCurrency,
      contractCount: contractPeriod,
      count: billingCyclePerMonths,
      avgPrice: displayAveragePrice,
    });
  }
};

/**
 * return all of the  price information for UI display.
 * @param {number} billingCyclePerMonths
 * @param {number} contractPeriod
 * @param {boolean} isContract
 * @param {number} price
 * @param {string} planType
 * @param {string} displayCurrency,
 * @param {string} region
 */

export const getSubscriptionPriceInfo = ({
  billingCyclePerMonths,
  contractPeriod,
  isContract,
  price,
  planType,
  currency,
  displayCurrency,
}) => {
  const {
    displayPrice,
    displayPriceUnit,
    displayPriceWithCurrency,
  } = getPriceUnitString({
    billingCyclePerMonths,
    contractPeriod,
    isContract,
    price,
    currency,
    displayCurrency,
  });

  const subscriptionDescription = getSubscriptionDescription({
    contractPeriod,
    isContract,
    billingCyclePerMonths,
    planType,
  });

  const priceDescription = getSubscriptionPriceDescription({
    contractPeriod,
    isContract,
    billingCyclePerMonths,
    planType,
    price,
    currency,
    displayPriceWithCurrency,
  });

  return {
    displayPrice,
    displayPriceUnit,
    subscriptionDescription,
    priceDescription,
  };
};



// WEBPACK FOOTER //
// ./app/utils/priceUnit.js